import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import io from 'socket.io-client';
const API_URL = process.env.REACT_APP_API_URL;
const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
    const { user, token } = useAuth();
    const [socket, setSocket] = useState(null);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [chatContext, setChatContext] = useState({});
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    
    const chatContextRef = useRef(chatContext);
    useEffect(() => { chatContextRef.current = chatContext; }, [chatContext]);
    const isChatOpenRef = useRef(isChatOpen);
    useEffect(() => { isChatOpenRef.current = isChatOpen; }, [isChatOpen]);

    const fetchUnreadCount = useCallback(async () => {
        if (!token || !user) { setUnreadCount(0); return; }
        try {
            const response = await fetch(`${API_URL}/api/chat/conversations/unread-count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUnreadCount(data.unreadCount || 0);
            }
        } catch (error) { console.error("Okunmamış mesaj sayısı alınamadı:", error); }
    }, [token, user]);
    
    useEffect(() => {
        if (user && token) {
            fetchUnreadCount();
            const newSocket = io(API_URL, { auth: { token } });
            setSocket(newSocket);
            newSocket.emit('join', user.id);

            const handleNewMessage = (newMessage) => {
                const currentContext = chatContextRef.current;
                if (String(currentContext.jobId) === String(newMessage.job_id)) {
                    setMessages(prev => prev.some(msg => msg.id === newMessage.id) ? prev : [...prev, newMessage]);
                }
            };
            const handleMessageUpdated = (updatedMessage) => { setMessages(prev => prev.map(msg => msg.id === updatedMessage.id ? updatedMessage : msg)); };
            
            const handleNewNotification = (notificationData) => {
                const currentContext = chatContextRef.current;
                // DÜZELTME: Sadece sohbetin kapalı olması değil, aynı zamanda farklı bir sohbete bakılıyor olması durumunu da kontrol et.
                if (isChatOpenRef.current && String(currentContext.jobId) === String(notificationData.jobId)) return;
                
                setNotifications(prev => {
                    if (prev.some(n => n.jobId === notificationData.jobId)) return prev;
                    return [...prev, notificationData];
                });
                // DÜZELTME: Sayıyı, backend'den gelen güncel değere göre ayarla
                setUnreadCount(prev => prev + 1); 
            };

            newSocket.on('newMessage', handleNewMessage);
            newSocket.on('messageUpdated', handleMessageUpdated);
            newSocket.on('new_message_notification', handleNewNotification);

            return () => { newSocket.disconnect(); setSocket(null); };
        }
    }, [user, token]);

    useEffect(() => {
        if (!user) { setNotifications([]); setUnreadCount(0); }
    }, [user]);
    const fetchMessages = useCallback(async (jobId) => {
        if (!jobId || !token) return;
        setLoadingMessages(true);
        try {
            const response = await fetch(`${API_URL}/api/chat/conversations/${jobId}/messages`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) {
              throw new Error(data.message || 'Mesajlar alınamadı.');
            }
            setMessages(data);
        } catch (error) {
            console.error("Mesajlar alınamadı:", error);
            setMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    }, [token]);

 const dismissNotification = (jobId) => { setNotifications(prev => prev.filter(n => n.jobId !== jobId)); };
	
    const openChat = useCallback(async (context = {}) => {
        setChatContext(context);
        setIsChatOpen(true);
        if (context.jobId) {
            if (socket) socket.emit('joinConversation', context.jobId);
            await fetchMessages(context.jobId);
            dismissNotification(context.jobId);
            try {
                await fetch(`${API_URL}/api/chat/conversations/${context.jobId}/mark-read`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                // DÜZELTME: Okundu işaretledikten sonra sayıyı güvenilir bir şekilde yeniden çek
                await fetchUnreadCount();
            } catch (error) { console.error("Mesajları okundu olarak işaretlerken hata oluştu:", error); }
        } else {
            setMessages([]);
        }
    }, [fetchMessages, token, fetchUnreadCount, socket]);
    const closeChat = () => {
        setIsChatOpen(false);
        setMessages([]);
        setChatContext({});
        fetchUnreadCount();
    };


    const sendMessage = async (text) => {
        if (!chatContext.jobId) return;
        try {
            await fetch(`${API_URL}/api/chat/conversations/${chatContext.jobId}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ content: text })
            });
        } catch (error) {
            console.error("Mesaj gönderilemedi:", error);
        }
    };


  const value = { socket, isChatOpen, unreadCount, messages, chatContext, loadingMessages, openChat, closeChat, sendMessage, fetchMessages, notifications, dismissNotification };
    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (context === null) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};